/**
 * Runtime URLs for I/O web actions (no client Authorization — Firefly auth is server-side like Surprise2Refine).
 * Initialized from surprise-bootstrap before app.js runs.
 */

let urls = {}

/** Parsed error body for failed `surpriseFetch` responses (body consumed from clone only). */
const surpriseFetchErrorDetails = new WeakMap()

/**
 * @param {{ urls: Record<string,string> }} cfg
 */
function setSurpriseRuntimeConfig (cfg) {
  urls = cfg.urls || {}
}

function surpriseUrl (key) {
  const u = urls[key]
  if (!u || !String(u).trim()) {
    throw new Error(
      'Missing Surprise action URL for "' + key + '". In the browser, URLs default to /api/v1/web/prj-testkitchen/<action>. Set SURPRISE_WEB_ACTIONS_BASE or SURPRISE_URL_* in .env and rebuild if your host does not proxy that path.'
    )
  }
  return u
}

/**
 * Human-readable summary of an API error body (JSON object, string, HTML error page, or empty).
 */
function summarizeSurpriseErrorBody (detail) {
  if (detail == null || detail === '') {
    return '(empty body — wrong URL/proxy often returns HTML 404 with no JSON)'
  }
  if (typeof detail === 'string') {
    const t = detail.trim()
    if (t.startsWith('<')) {
      const snippet = t.length > 240 ? t.slice(0, 240) + '…' : t
      return 'HTML response (not JSON): ' + snippet + ' — check SURPRISE_* action URL / aio proxy'
    }
    if (t.length > 600) return t.slice(0, 600) + '…'
    return t
  }
  const o = detail
  const parts = []
  if (typeof o === 'object' && o !== null && !Array.isArray(o)) {
    if (o.hint) parts.push(String(o.hint))
    if (o.upstreamUrl) {
      parts.push(
        'upstream ' +
          (o.upstreamMethod ? o.upstreamMethod + ' ' : '') +
          String(o.upstreamUrl) +
          (o.upstreamStatus != null ? ' → HTTP ' + o.upstreamStatus : '')
      )
    }
  }
  if (o.message != null) parts.push(String(o.message))
  if (o.error != null) {
    parts.push(typeof o.error === 'string' ? o.error : JSON.stringify(o.error))
  }
  if (o.title != null) parts.push(String(o.title))
  if (o.error_code != null) parts.push('error_code=' + o.error_code)
  if (o.details != null) {
    try {
      parts.push(typeof o.details === 'string' ? o.details : JSON.stringify(o.details))
    } catch {
      parts.push('details=(unserializable)')
    }
  }
  if (parts.length) return parts.join(' | ')
  try {
    return JSON.stringify(o)
  } catch {
    return String(o)
  }
}

function logSurpriseHttpFailure ({ actionKey, method, url, status, statusText, detail }) {
  const summary = summarizeSurpriseErrorBody(detail)
  console.groupCollapsed(`[Surprise API] FAILED ${actionKey} — HTTP ${status} ${statusText || ''}`)
  console.error('Summary (what to fix):', summary)
  console.error('Full response body:', detail)
  console.error('Request:', { actionKey, method: method || 'GET', url })
  console.groupEnd()
}

/**
 * Log a failed jQuery.ajax / $.ajax call in one place (expand the group in DevTools for full body).
 * @param {{ label?: string, url?: string, jqXHR: object, textStatus?: string, errorThrown?: string }} p
 */
function logJqueryAjaxFailure ({ label, url, jqXHR, textStatus, errorThrown }) {
  if (!jqXHR || errorThrown === 'abort' || textStatus === 'abort') return
  const status = jqXHR.status
  let detail = jqXHR.responseJSON
  if (detail === undefined && jqXHR.responseText) {
    try {
      detail = JSON.parse(jqXHR.responseText)
    } catch {
      detail = jqXHR.responseText
    }
  }
  const effUrl = url || jqXHR.responseURL || ''
  const summary = summarizeSurpriseErrorBody(detail)
  const tag = label || 'AJAX'
  console.groupCollapsed(`[Surprise API] FAILED ${tag} — HTTP ${status} ${jqXHR.statusText || ''}`)
  console.error('Summary (what to fix):', summary)
  console.error('Full response body:', detail)
  console.error('Request:', { url: effUrl, textStatus, errorThrown })
  console.groupEnd()
}

/**
 * One-line message for alerts and onError() callbacks after a failed $.ajax.
 */
function formatJqueryAjaxFailureMessage (jqXHR, textStatus, errorThrown) {
  if (textStatus === 'abort') return 'Request cancelled'
  if (!jqXHR || typeof jqXHR.status !== 'number') {
    return (errorThrown && String(errorThrown)) || (textStatus && String(textStatus)) || 'Request failed'
  }
  let detail = jqXHR.responseJSON
  if (detail === undefined && jqXHR.responseText) {
    try {
      detail = JSON.parse(jqXHR.responseText)
    } catch {
      detail = jqXHR.responseText
    }
  }
  return `HTTP ${jqXHR.status}: ${summarizeSurpriseErrorBody(detail)}`
}

/** Rejected value from `await $.ajax()` is usually a jqXHR; normalize for user-visible messages. */
function formatAjaxRejectionForUser (error, textStatus) {
  if (error && typeof error.status === 'number' && error.responseText !== undefined) {
    return formatJqueryAjaxFailureMessage(error, textStatus || 'error', '')
  }
  if (error && error.message) return error.message
  return String(error)
}

function getSurpriseFetchErrorDetail (response) {
  return surpriseFetchErrorDetails.get(response)
}

function surpriseFetch (key, options) {
  const url = surpriseUrl(key)
  const method = (options && options.method) || 'GET'
  const headers = Object.assign(
    { 'Content-Type': 'application/json' },
    (options && options.headers) || {}
  )
  return fetch(url, Object.assign({}, options, { headers })).then(async (response) => {
    if (!response.ok) {
      let detail = null
      try {
        const text = await response.clone().text()
        if (text) {
          try {
            detail = JSON.parse(text)
          } catch {
            detail = text
          }
        }
      } catch {
        detail = '(could not read response body)'
      }
      surpriseFetchErrorDetails.set(response, detail)
      logSurpriseHttpFailure({
        actionKey: key,
        method,
        url,
        status: response.status,
        statusText: response.statusText,
        detail
      })
    }
    return response
  })
}

module.exports = {
  setSurpriseRuntimeConfig,
  surpriseUrl,
  surpriseFetch,
  summarizeSurpriseErrorBody,
  getSurpriseFetchErrorDetail,
  logJqueryAjaxFailure,
  formatJqueryAjaxFailureMessage,
  formatAjaxRejectionForUser
}
